export type NavigationGroup = "pages" | "collections" | "products" | "articles";

/** The order in which the Navigator lists its groups. */
export const NAVIGATION_GROUPS: readonly NavigationGroup[] = [
  "pages",
  "collections",
  "products",
  "articles",
];

/**
 * The number of items per group in the first page. Query one more than this
 * (`first: NAVIGATION_PAGE_SIZE + 1`): the extra edge tells that the group has
 * more, and it is not listed.
 */
export const NAVIGATION_PAGE_SIZE = 10;

/**
 * The number of items that one "load more" adds to a group. Query one more
 * than this, for the same reason as `NAVIGATION_PAGE_SIZE`.
 */
export const NAVIGATION_LOAD_MORE_SIZE = 25;

/** The group of items that belong to no Storefront type, such as Home. */
export const STANDALONE_NAVIGATION_GROUP = "";

export interface NavigationItem {
  title: string;
  /** An absolute path that starts with the locale root. */
  path: string;
  group: NavigationGroup | typeof STANDALONE_NAVIGATION_GROUP;
}

export interface NavigationGroupState {
  items: NavigationItem[];
  endCursor: string | null;
  hasMore: boolean;
}

/**
 * The narrow connection shape the Navigator reads. Written by hand because
 * ig-clients has no GraphQL codegen; each app's generated Storefront query
 * types are structurally assignable to it.
 */
export interface NavigationConnection<TNode> {
  edges: ReadonlyArray<{ cursor: string; node: TNode }>;
}

interface HandleNode {
  title: string;
  /** The handle in the `@inContext` language. */
  handle: string;
}

interface ArticleNode extends HandleNode {
  blog: { handle: string };
}

/** A Storefront navigation response: one connection per group. */
export interface NavigationResponse {
  pages: NavigationConnection<HandleNode>;
  collections: NavigationConnection<HandleNode>;
  products: NavigationConnection<HandleNode>;
  articles: NavigationConnection<ArticleNode>;
}

export interface BuildNavigationItemsInput {
  /** The locale root as Shopify reports it (`routes.root`): `/` or `/fr/`. */
  root: string;
  /** Undefined until the first response arrives. */
  response: NavigationResponse | undefined;
  /** The items that "load more" added, by group. Their paths already include the root. */
  extraItems: Partial<Record<NavigationGroup, NavigationGroupState>>;
}

export interface NavigationItems {
  options: NavigationItem[];
  groupHasMore: Partial<Record<NavigationGroup, boolean>>;
  groupEndCursor: Partial<Record<NavigationGroup, string | null>>;
}

/** The path of an item: the locale root, its type and its translated handle. */
function itemPath(
  root: string,
  group: NavigationGroup,
  node: HandleNode | ArticleNode
): string {
  return "blog" in node
    ? `${root}blogs/${node.blog.handle}/${node.handle}`
    : `${root}${group}/${node.handle}`;
}

/**
 * One page of a group. The query asks for one edge more than `pageSize`: that
 * edge is not listed, and it tells that the group has more.
 */
function groupPage(
  root: string,
  group: NavigationGroup,
  edges: NavigationResponse[NavigationGroup]["edges"],
  pageSize: number
): NavigationGroupState {
  const visibleEdges = edges.slice(0, pageSize);
  return {
    items: visibleEdges.map(({ node }) => ({
      title: node.title,
      path: itemPath(root, group, node),
      group,
    })),
    hasMore: edges.length > pageSize,
    endCursor: visibleEdges.at(-1)?.cursor ?? null,
  };
}

export interface AddLoadMorePageInput {
  /** The locale root as Shopify reports it (`routes.root`): `/` or `/fr/`. */
  root: string;
  group: NavigationGroup;
  /** The items that earlier "load more" pages added to this group. */
  loaded: NavigationGroupState | undefined;
  /** The group's connection in the load-more response. */
  connection: NavigationResponse[NavigationGroup];
}

/**
 * Adds one "load more" page to the items already loaded for a group. The new
 * items follow the loaded ones, and the page decides the group's `hasMore`
 * and end cursor. An empty page keeps the last cursor.
 */
export function addLoadMorePage({
  root,
  group,
  loaded,
  connection,
}: AddLoadMorePageInput): NavigationGroupState {
  const page = groupPage(
    root,
    group,
    connection.edges,
    NAVIGATION_LOAD_MORE_SIZE
  );
  return {
    items: [...(loaded?.items ?? []), ...page.items],
    hasMore: page.hasMore,
    endCursor: page.endCursor ?? loaded?.endCursor ?? null,
  };
}

type GroupPages = Partial<Record<NavigationGroup, NavigationGroupState>>;

/**
 * Lists the first page of each group, in the Navigator's group order. The
 * "load more" items of a group follow its first page, and their state
 * replaces the group's `hasMore` and end cursor. A group with no first page
 * is not listed.
 */
function listGroups(
  options: NavigationItem[],
  pages: GroupPages,
  extraItems: GroupPages
): NavigationItems {
  const groupHasMore: NavigationItems["groupHasMore"] = {};
  const groupEndCursor: NavigationItems["groupEndCursor"] = {};
  for (const group of NAVIGATION_GROUPS) {
    const page = pages[group];
    if (!page) {
      continue;
    }
    const extra = extraItems[group];
    options.push(...page.items, ...(extra?.items ?? []));
    groupHasMore[group] = (extra ?? page).hasMore;
    groupEndCursor[group] = (extra ?? page).endCursor;
  }
  return { options, groupHasMore, groupEndCursor };
}

/**
 * Converts a Storefront navigation response (no query) to Navigator items:
 * Home, then every group. Every path starts with `root`, so navigation keeps
 * the preview's locale root.
 */
export function buildNavigationItems({
  root,
  response,
  extraItems,
}: BuildNavigationItemsInput): NavigationItems {
  const home: NavigationItem = {
    title: "Home",
    path: root,
    group: STANDALONE_NAVIGATION_GROUP,
  };
  const pages: GroupPages = {};
  if (response) {
    for (const group of NAVIGATION_GROUPS) {
      pages[group] = groupPage(
        root,
        group,
        response[group].edges,
        NAVIGATION_PAGE_SIZE
      );
    }
  }
  return listGroups([home], pages, extraItems);
}

/**
 * A typed-search response. `search` gives one connection per group that it
 * searched (pages, products, articles), each with cursors.
 * `predictiveSearch` gives collections with no cursors: at most 10 and no
 * "load more". A group that was not searched is absent and is not listed.
 */
export interface NavigationSearchResponse {
  pages?: NavigationConnection<HandleNode | ArticleNode>;
  products?: NavigationConnection<HandleNode | ArticleNode>;
  articles?: NavigationConnection<HandleNode | ArticleNode>;
  predictiveSearch?: { collections: ReadonlyArray<HandleNode> } | null;
}

export interface BuildSearchItemsInput {
  /** The locale root as Shopify reports it (`routes.root`): `/` or `/fr/`. */
  root: string;
  /** Undefined until the first response arrives. */
  response: NavigationSearchResponse | undefined;
  /** The items that "load more" added, by group. Their paths already include the root. */
  extraItems: GroupPages;
}

/** The first page of each group in a typed-search response. */
function searchPages(
  root: string,
  response: NavigationSearchResponse
): GroupPages {
  const pages: GroupPages = {};
  for (const group of NAVIGATION_GROUPS) {
    if (group === "collections") {
      if (response.predictiveSearch === undefined) {
        continue;
      }
      pages.collections = {
        items: (response.predictiveSearch?.collections ?? []).map((node) => ({
          title: node.title,
          path: itemPath(root, group, node),
          group,
        })),
        hasMore: false,
        endCursor: null,
      };
      continue;
    }
    const connection = response[group];
    if (connection) {
      pages[group] = groupPage(
        root,
        group,
        connection.edges,
        NAVIGATION_PAGE_SIZE
      );
    }
  }
  return pages;
}

/**
 * Converts a typed-search response to Navigator items, in the Navigator's
 * group order. Every path starts with `root`. Home is not listed.
 */
export function buildSearchItems({
  root,
  response,
  extraItems,
}: BuildSearchItemsInput): NavigationItems {
  return listGroups(
    [],
    response ? searchPages(root, response) : {},
    extraItems
  );
}
